import {Component, HostListener, Input, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {HoomvipLogoComponent} from '../../shared/components/hoomvip-logo/hoomvip-logo.component';
import {AuthService} from '../../core/auth/auth.service';
import {RoundButtonComponent} from '../../shared/components/round-button/round-button.component';
import {ThemeService} from '../../core/services/theme.service';
import {LangSelectorComponent} from '../../shared/components/lang-selector/lang-selector.component';
import {catchError} from 'rxjs/operators';
import {of} from 'rxjs';
import {RouterService} from '../../core/services/router.service';
import {RouterLink} from '@angular/router';
import {BrandingContactService, BrandingContactAction, openAction} from '../../core/services/branding-contact.service';
import {ModalErrorService} from '../../core/services/modal-error.service';
import {GlobalErrorService} from '../../core/services/global-error.service';
import {ModalInstallInfoComponent} from '../../shared/components/modal-install-info/modal-install-info.component';
import {HoomvipStorage} from '../../core/hoomvip-storage/hoomvip-storage.service';

@Component({
  selector: 'hv-login',
  standalone: true,
  imports: [CommonModule, FormsModule, HoomvipLogoComponent, RoundButtonComponent, ReactiveFormsModule, LangSelectorComponent, RouterLink, ModalInstallInfoComponent],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {

  @Input() disabled: boolean = false;
  form: FormGroup;
  language = 'Español';
  loading = signal(false);

  showInstallInfoModal = signal(false);

  isInstalled = signal(true);

  private deferredPrompt: any;

  topActions: BrandingContactAction[] = [];

  loginModalTitle = 'Error';
  loginModalMessages: string[] = [];

  constructor(
    private fb: FormBuilder,
    private router: RouterService,
    private auth: AuthService,
    private theme: ThemeService,
    private contact: BrandingContactService,
    private modalErrorService: ModalErrorService,
    private globalErrorService: GlobalErrorService,
    private hoomvipStorage: HoomvipStorage,
  ) {
    this.form = this.fb.group({
      guestCode: ['', Validators.required],
      aceptaCondiciones: [false, Validators.requiredTrue],
    });

    this.form.get('guestCode')?.valueChanges.subscribe(value => {
      if (value && typeof value === 'string') {
        this.form.get('guestCode')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    const isRunningAsApp =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    this.isInstalled.set(isRunningAsApp);

    if (!isRunningAsApp && !this.hoomvipStorage.getInstallWarning()) {
      this.openInstallationModal();
      this.hoomvipStorage.markInstallWarning();
    }
    this.loadTopActions();
  }

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstallPrompt(event: Event): void {
    event.preventDefault();
    this.deferredPrompt = event;
  }

  protected installPWA(): void {
    if (!this.deferredPrompt) {
      return;
    }

    this.deferredPrompt.prompt();
    this.deferredPrompt.userChoice
      .then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('El usuario aceptó la instalación');
        }
        this.deferredPrompt = null;
      })
      .catch((error: any) => {
        console.error('Error al instalar la PWA:', error);
      });
  }

  private loadTopActions():void {
    this.contact.loadBrandingContactActions().subscribe(actions => {
      this.topActions = actions;
    });
  }

  getLogoPath(): string {
    return this.theme.getLogoPath();
  }

  onSubmit() {
    const guestCodeValue = this.form.get('guestCode')?.value?.trim() || '';
    const aceptaCondiciones = this.form.get('aceptaCondiciones')?.value;
    const validationErrors: string[] = [];

    if (!guestCodeValue) {
      validationErrors.push('Debes introducir tu código de huésped.');
    }

    if (!aceptaCondiciones) {
      validationErrors.push('Debes aceptar los Términos y Condiciones.');
    }

    if (validationErrors.length) {
      this.enqueueValidationErrorsModal(validationErrors);
      return;
    }

    this.loading.set(true);
    this.auth.login(guestCodeValue).pipe(
      catchError(error => {
        this.globalErrorService.markHandled(error);
        return of(null);
      })
    ).subscribe(result => {
      this.loading.set(false);
      if (result) {
        this.router.goToHome();
      } else {
        this.enqueueInvalidCodeModal();
      }
    });
  }

  disabledSubmit()
  {
    return this.loading();
  }

  private enqueueInvalidCodeModal() {
    this.modalErrorService.enqueue(
      'Código no autorizado',
      'Tu código de huésped ha caducado o es erróneo. Contacta con tu anfitrión'
    );
  }

  private enqueueValidationErrorsModal(errors: string[]) {
    this.modalErrorService.enqueue(
      'Error de validación',
      errors
    );
  }

  protected openInstallationModal() {
    this.showInstallInfoModal.set(true);
  }

  protected closeInstallInfoModal() {
    this.showInstallInfoModal.set(false);
  }

  protected readonly openAction = openAction;
}

